import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/css/GuesthouseCheckInCheckOut.css";
import GuesthouseAdminEditBooking from "./GuesthouseAdminEditBooking";
import GuesthouseStaffEditBooking from "./GuesthouseStaffEditBooking";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const LEGACY_PORTAL_BASE = (process.env.REACT_APP_LEGACY_PORTAL_BASE || "http://127.0.0.1").replace(/\/$/, "");
const GH_API = API_ROOT.endsWith("/api")
  ? `${API_ROOT}/guesthouse`
  : `${API_ROOT}/api/guesthouse`;

const toInputDate = (d) => d.toISOString().slice(0, 10);

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit"
  });
}

function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const date = d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit"
  });
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });
  return `${date} ${time}`;
}

function getRowColor(row) {
  if (row?.RowColor) {
    if (row.RowColor === "lightcoral") return "#f4a6a6";
    if (row.RowColor === "lightgreen") return "#bde7b7";
    return row.RowColor;
  }

  const mode = String(row?.PaymentMode || "").trim().toUpperCase();
  if (mode === "P") return "#f4a6a6";
  if (["C", "Q", "O"].includes(mode)) return "#bde7b7";
  return "";
}

function getPaymentModeCode(row) {
  const code = String(row?.PaymentMode || "").trim().toUpperCase();
  if (code) return code;

  const modeText = String(row?.PaymentModeText || "").trim().toLowerCase();
  if (modeText === "pending") return "P";
  if (modeText === "cash") return "C";
  if (modeText === "cheque") return "Q";
  if (modeText === "online") return "O";
  return "";
}

function getRowClass(row) {
  const mode = getPaymentModeCode(row);
  if (mode === "P") return "checkin-row-red";
  if (["C", "Q", "O"].includes(mode)) return "checkin-row-green";
  return "";
}

export default function GuesthouseCheckInCheckOut() {
  const navigate = useNavigate();

  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const moduleId = parseInt(user?.moduleId, 10);

  const today = useMemo(() => new Date(), []);

  const [guestHouses, setGuestHouses] = useState([]);
  const [guestHouseID, setGuestHouseID] = useState("");
  const [fromDate, setFromDate] = useState(toInputDate(today));
  const [toDate, setToDate] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editBookingId, setEditBookingId] = useState(null);

  useEffect(() => {
    if (![10, 11, 22, 23, 27, 28].includes(moduleId)) {
      navigate("/dashboard", { replace: true });
    }
  }, [moduleId, navigate]);

  useEffect(() => {
    const load = async () => {
      try {
        const res  = await fetch(`${GH_API}/options?moduleId=${moduleId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Failed to load guest houses");
        if (Array.isArray(data) && data.length) {
          setGuestHouses(data);
          setGuestHouseID(String(data[0].GuestHouseID));
        }
      } catch (e) {
        setError(e.message);
      }
    };
    if ([10, 11, 22, 23, 27, 28].includes(moduleId)) load();
  }, [moduleId]);

  const fetchRecords = async () => {
    setError("");
    if (!guestHouseID) { setError("Please select a Guest House."); return; }

    try {
      setLoading(true);
      const q = new URLSearchParams({
        moduleId: String(moduleId || ""),
        guestHouseID: String(guestHouseID || ""),
        fromDate: String(fromDate || "")
      });
      if (toDate) {
        q.set("toDate", toDate);
      }

      const res = await fetch(`${GH_API}/checkin-checkout/records?${q.toString()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch records");

      setRows(Array.isArray(data?.rows) ? data.rows : []);

      if (!toDate && data?.toDate) {
        setToDate(String(data.toDate));
      }
    } catch (e) {
      setRows([]);
      setError(e.message || "Failed to fetch records");
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = () => {
    if (!guestHouseID) {
      setError("Please select a Guest House.");
      return;
    }
    if (!toDate) {
      setError("Please select To Date");
      return;
    }

    const q = new URLSearchParams({
      moduleId: String(moduleId || ""),
      guestHouseID: String(guestHouseID || ""),
      fromDate: String(fromDate || ""),
      toDate: String(toDate || "")
    });
    window.location.href = `${GH_API}/checkin-checkout/export?${q.toString()}`;
  };

  const openPrintForm = (bookingId) => {
    const q = new URLSearchParams({
      moduleId: String(moduleId || "")
    });
    const printUrl = `${GH_API}/booking/print/${encodeURIComponent(bookingId)}?${q.toString()}`;
    window.open(printUrl, "_blank", "noopener,noreferrer");
  };

  const openInvoice = (bookingId) => {
    const q = new URLSearchParams({ moduleId: String(moduleId || "") });
    window.open(`${GH_API}/booking/invoice-pdf/${encodeURIComponent(bookingId)}?${q}`, "_blank", "noopener,noreferrer");
  };

  const openProforma = (bookingId) => {
    window.open(`${GH_API}/booking/proforma-pdf/${encodeURIComponent(bookingId)}?moduleId=${moduleId}`, "_blank", "noopener,noreferrer");
  };

  const openLetter = (bookingId, bookingRef) => {
    const q = new URLSearchParams({ moduleId: String(moduleId || "") });
    if (bookingRef) q.set("bookingRef", bookingRef);
    window.open(`${GH_API}/booking/invitation-letter/${encodeURIComponent(bookingId)}?${q}`, "_blank", "noopener,noreferrer");
  };

  const openEdit = (bookingId) => {
    setEditBookingId(bookingId);
  };

  const viewAttachment = async (supportDoc) => {
    if (!supportDoc) {
      window.alert("No attachment found.");
      return;
    }

    const rawPath = String(supportDoc || "").trim();
    if (!rawPath) {
      window.alert("No attachment found.");
      return;
    }

    const normalizedRaw = rawPath.replace(/\\/g, "/");

    if (!normalizedRaw.includes("/") || /ghbooking_request/i.test(normalizedRaw)) {
      const fileName = normalizedRaw.split("/").pop();
      if (!fileName) {
        window.alert("Attachment URL could not be resolved.");
        return;
      }

      const url = `${API_ROOT}/api/guesthouse/ghbooking_request/${encodeURIComponent(fileName)}`;
      window.open(url, "_blank");
      return;
    }

    const normalizedPath = normalizedRaw.replace(/^\/+/, "");
    const encodedPath = normalizedPath.split("/").map(encodeURIComponent).join("/");

    if (/^https?:\/\//i.test(rawPath)) {
      window.location.href = rawPath;
      return;
    }

    const candidates = [
      API_ROOT ? `${API_ROOT}/${encodedPath}` : "",
      `${LEGACY_PORTAL_BASE}/${encodedPath}`
    ].filter(Boolean);

    for (const candidate of candidates) {
      try {
        const res = await fetch(candidate, { method: "HEAD" });
        if (res.ok) {
          window.location.href = candidate;
          return;
        }
      } catch {
      }
    }

    if (candidates.length) {
      window.location.href = candidates[0];
      return;
    }

    window.alert("Attachment URL could not be resolved.");
  };

  return (
    <>
    <section className="container-fluid">
      <h3 className="ctitle mb-3">CheckIn-CheckOut Record</h3>

      <form onSubmit={(e) => { e.preventDefault(); fetchRecords(); }} className="mb-3">
        <table style={{ borderCollapse: 'collapse', width: 'auto', border: '0' }}>
          <tbody>
            <tr style={{ border: '0' }}>
              <td style={{ border: '0', padding: '0 8px 4px 0' }}><label className="form-label fw-semibold mb-0">Guest House</label></td>
              <td style={{ border: '0', padding: '0 8px 4px 0' }}><label className="form-label fw-semibold mb-0">From Date</label></td>
              <td style={{ border: '0', padding: '0 8px 4px 0' }}><label className="form-label fw-semibold mb-0">To Date</label></td>
              <td style={{ border: '0', padding: '0 8px 4px 0' }}><label className="form-label fw-semibold mb-0">View</label></td>
              {moduleId === 10 && (
                <td style={{ border: '0', padding: '0 0 4px 0' }}><label className="form-label fw-semibold mb-0">Download</label></td>
              )}
            </tr>
            <tr style={{ border: '0' }}>
              <td style={{ border: '0', padding: '0 8px 0 0' }}>
                <select
                  className="form-select form-select-sm"
                  value={guestHouseID}
                  onChange={(e) => setGuestHouseID(e.target.value)}
                  required
                >
                  <option value="">Select Guest House</option>
                  {guestHouses.map((g) => (
                    <option key={g.GuestHouseID} value={g.GuestHouseID}>
                      {g.GuestHouse}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ border: '0', padding: '0 8px 0 0' }}>
                <input
                  className="form-control form-control-sm"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  required
                />
              </td>
              <td style={{ border: '0', padding: '0 8px 0 0' }}>
                <input
                  className="form-control form-control-sm"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </td>
              <td style={{ border: '0', padding: '0 8px 0 0', verticalAlign: 'bottom' }}>
                <button className="btn btn-primary btn-sm" type="submit" disabled={loading} style={{ padding: '5px 20px' }}>
                  {loading ? "Loading..." : "View"}
                </button>
              </td>
              {moduleId === 10 && (
                <td style={{ border: '0', padding: '0', verticalAlign: 'bottom' }}>
                  <button className="btn btn-success btn-sm" type="button" onClick={downloadExcel} style={{ padding: '5px 20px' }}>
                    Download
                  </button>
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </form>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="table-responsive">
        <table className="table table-bordered table-sm align-middle" style={{ whiteSpace: "nowrap" }}>
          <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
            <tr>
              <th>SNo</th>
              <th>BookID</th>
              <th>BookedBy</th>
              <th>Visitor Name</th>
              <th>Room</th>
              <th>FromDate</th>
              <th>ToDate</th>
              <th>ChkIn</th>
              <th>ChkOut</th>
              <th>Amt</th>
              <th>GST</th>
              <th>TAmt</th>
              <th>InvoiceNo</th>
              <th>InvDt</th>
              <th>Mode</th>
              <th>TransactionID</th>
              <th>Status</th>
              <th>Form</th>
              <th>Invoice</th>
              <th>Doc</th>
              <th>Edit</th>
              <th>Proforma</th>
              {(guestHouseID === "4" || guestHouseID === "5") && <th>Letter</th>}
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={22} className="text-center text-muted py-3">
                  No records found.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={22} className="text-center text-muted py-3">
                  Loading...
                </td>
              </tr>
            )}
            {rows.map((row, idx) => {
              const rowColor = getRowColor(row);
              const rowClass = getRowClass(row);

              return (
              <tr key={`${row.BookingID}-${idx}`} className={rowClass} style={{ backgroundColor: rowColor || "", color: "#111" }}>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 600 }}>{idx + 1}</td>
                <td
                  className="text-decoration-underline"
                  style={{ cursor: "pointer", backgroundColor: rowColor || "", color: "#111", fontWeight: 600 }}
                  onClick={() => openEdit(row.BookingID)}
                  title="Open Edit"
                >
                  {row.BookingIDDisplay || row.BookingID}
                </td>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 500 }}>{row.BookedBy || ""}</td>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 500 }}>{row.VisitorName || ""}</td>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 500 }}>{row.Room || ""}</td>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 500 }}>{formatDate(row.FromDate)}</td>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 500 }}>{formatDate(row.ToDate)}</td>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 500 }}>{formatDateTime(row.CheckIn)}</td>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 500 }}>{formatDateTime(row.CheckOut)}</td>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 500 }}>{row.Amount ?? 0}</td>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 500 }}>{row.GSTAmount ?? 0}</td>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 500 }}>{row.TotalAmount ?? 0}</td>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 500 }}>{row.ReceiptNo || ""}</td>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 500 }}>{formatDate(row.ReceiptDate)}</td>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 500 }}>{row.PaymentModeText || ""}</td>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 500 }}>{row.UPIID || ""}</td>
                <td style={{ backgroundColor: rowColor || "", color: "#111", fontWeight: 500 }}>{row.Status || ""}</td>
                <td>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a href="#" style={{ color: "#000" }} onClick={e => { e.preventDefault(); openPrintForm(row.BookingID); }}>Form</a>
                </td>
                <td>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a href="#" style={{ color: "#000" }} onClick={e => { e.preventDefault(); openInvoice(row.BookingID); }}>Invoice</a>
                </td>
                <td>
                  {row.SupportDoc ? (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <a href="#" onClick={e => { e.preventDefault(); viewAttachment(row.SupportDoc); }}>Doc</a>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a href="#" style={{ color: "#000" }} onClick={e => { e.preventDefault(); openEdit(row.BookingID); }}>Edit</a>
                </td>
                <td>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a href="#" style={{ color: "#000" }} onClick={e => { e.preventDefault(); openProforma(row.BookingID); }}>Proforma</a>
                </td>
                {(guestHouseID === "4" || guestHouseID === "5") && (
                  <td>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a href="#" style={{ color: "#000" }} onClick={e => { e.preventDefault(); openLetter(row.BookingID, row.BookingRef); }}>Letter</a>
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>

    {editBookingId && (
      [11, 22, 23, 27, 28].includes(moduleId) ? (
        <GuesthouseStaffEditBooking
          bookingId={editBookingId}
          moduleId={moduleId}
          onClose={() => setEditBookingId(null)}
          onSaved={() => { setEditBookingId(null); fetchRecords(); }}
        />
      ) : (
        <GuesthouseAdminEditBooking
          bookingId={editBookingId}
          moduleId={moduleId}
          onClose={() => setEditBookingId(null)}
          onSaved={() => { setEditBookingId(null); fetchRecords(); }}
        />
      )
    )}
    </>
  );
}